import React, { useEffect, useState } from "react";
import { Tooltip, useTheme } from "@mui/material";
import { FiAlertCircle, FiLock, FiMinus, FiPackage, FiPlus, FiTrash2 } from "react-icons/fi";
import { ConfirmDialog, ErrorBox, OrgModal, PrimaryButton, SecondaryButton, Section } from "../organisation/OrgModal";
import { METRIC_ICONS, Skeleton, fieldClass, useFieldStyle } from "./subscriptionUi";
import useResetOnReturn from "../../hooks/useResetOnReturn";
import {
  addonGain,
  apiErrorCode,
  apiErrorMessage,
  buyAddon,
  formatCents,
  formatDate,
  formatNumber,
  metricForAddon,
  metricLabel,
  PLAN_STATE,
  planOf,
  previewAddon,
  refreshPlan,
  rememberCheckoutBaseline,
  removeAddon,
} from "../../utils/subscriptionData";

const MAX_QUANTITY = 100;

const BUY_ERRORS = {
  ADDON_NOT_AVAILABLE: "That add-on isn't offered on your plan.",
  SUBSCRIPTION_REQUIRED: "Seats need a paid subscription. Upgrade your plan to add seats.",
  NO_ACTIVE_PLAN: "Choose a plan before buying add-ons.",
};

const IconTile = ({ type }) => {
  const Icon = METRIC_ICONS[metricForAddon(type)] || FiPackage;
  return (
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#f3f6fb] text-accent-blue" aria-hidden="true">
      <Icon className="h-4 w-4" />
    </span>
  );
};

const BillingTag = ({ recurring }) => (
  <span className={`rounded px-1.5 text-[10px] font-medium uppercase leading-4 tracking-wide ${recurring ? "bg-blue-100 text-blue-800" : "bg-gray-100 text-gray-700"}`}>
    {recurring ? "Monthly" : "One time"}
  </span>
);

const stepButton =
  "flex h-7 w-7 items-center justify-center rounded-md border bg-white hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black";

// One add-on you can buy, as a single slim row. Prices and pack sizes come from the current plan in GET /plans.
// Buying opens a confirmation dialog, so the row never changes size.
const AddonOffer = ({ type, addon, onBuy }) => {
  const theme = useTheme();
  const inputStyle = useFieldStyle();
  const [quantity, setQuantity] = useState("1");
  const count = Number(quantity);
  const valid = Number.isInteger(count) && count >= 1 && count <= MAX_QUANTITY;
  const recurring = addon.billing === "recurring";
  const step = (delta) => setQuantity(String(Math.min(MAX_QUANTITY, Math.max(1, (valid ? count : 1) + delta))));

  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-2 py-2">
      <IconTile type={type} />
      <div className="min-w-[11rem] flex-1">
        <p className="flex flex-wrap items-center gap-x-2 text-sm font-semibold">
          {addon.name}
          <BillingTag recurring={recurring} />
        </p>
        <p className="text-xs text-accent-gray-light">{addonGain(addon.units, metricForAddon(type))}</p>
      </div>
      <p className="w-20 shrink-0 text-right text-base font-bold leading-tight">
        {formatCents(addon.amount_cents)}
        <span className="block text-[10px] font-normal text-accent-gray-light">{recurring ? "per month" : "one time"}</span>
      </p>
      {/* Quantity and Buy stay together. On a narrow screen they take their own line, left-aligned under the name (pl-11
          matches the icon and gap); from a large screen up they sit on the same line as the price */}
      <div className="flex shrink-0 basis-full items-center gap-2 pl-11 lg:basis-auto lg:pl-0">
      <div role="group" aria-label={`${addon.name} quantity`} className="flex items-center gap-0.5">
        <button type="button" className={stepButton} style={{ borderColor: theme.palette.secondary.v4 }} onClick={() => step(-1)} disabled={valid && count <= 1} aria-label="Decrease quantity">
          <FiMinus className="h-3 w-3" aria-hidden="true" />
        </button>
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={String(MAX_QUANTITY).length}
          value={quantity}
          // Digits only, never above the maximum; an empty box is allowed while typing and tidied when it loses focus
          onChange={(e) => {
            const digits = e.target.value.replace(/\D/g, "");
            setQuantity(digits === "" ? "" : String(Math.min(MAX_QUANTITY, Number(digits))));
          }}
          onBlur={() => {
            if (!(Number(quantity) >= 1)) setQuantity("1");
          }}
          title={`Maximum ${MAX_QUANTITY}`}
          aria-label={`Quantity, maximum ${MAX_QUANTITY}`}
          aria-invalid={!valid}
          className={`${fieldClass} !h-7 !w-14 !px-1 !py-0 text-center text-sm tabular-nums`}
          style={inputStyle}
        />
        <button type="button" className={stepButton} style={{ borderColor: theme.palette.secondary.v4 }} onClick={() => step(1)} disabled={valid && count >= MAX_QUANTITY} aria-label="Increase quantity">
          <FiPlus className="h-3 w-3" aria-hidden="true" />
        </button>
      </div>
      <SecondaryButton onClick={() => onBuy({ type, addon, count })} disabled={!valid} sx={{ minWidth: "5.5rem" }}>
        Buy {valid ? formatCents(addon.amount_cents * count) : ""}
        <span className="sr-only"> {addon.name}</span>
      </SecondaryButton>
      </div>
    </li>
  );
};

// An add-on the organisation owns, as a slim read-only row like the ones you buy from: icon, name and tag,
// one line of detail, the cost on the right.
export const OwnedAddonRow = ({ addon, unitCents, onRemove }) => {
  const metric = metricForAddon(addon.addon_type);
  const removing = addon.status === "canceling";
  const recurring = addon.billing === "recurring";
  const details = [
    addonGain(addon.units, metric),
    removing
      ? `removed, available until ${formatDate(addon.end_at)}`
      : addon.end_at
        ? `${recurring ? "ends" : "valid until"} ${formatDate(addon.end_at)}`
        : `bought ${formatDate(addon.start_at)}`,
  ];
  return (
    <li className="flex items-center gap-3 py-2">
      <IconTile type={addon.addon_type} />
      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-center gap-x-2 text-sm font-semibold">
          {addon.name}
          <BillingTag recurring={recurring} />
          {removing && <span className="rounded bg-amber-100 px-1.5 text-[10px] font-medium uppercase leading-4 tracking-wide text-amber-800">Removed</span>}
        </p>
        <p className="text-xs text-accent-gray-light">{details.join(" · ")}</p>
      </div>
      {typeof unitCents === "number" && (
        <p className="min-w-[3.5rem] shrink-0 text-right text-sm font-semibold leading-tight">
          {formatCents(unitCents * (Number(addon.quantity) || 0))}
          <span className="block text-[10px] font-normal text-accent-gray-light">{recurring ? "per month" : "one time"}</span>
        </p>
      )}
      {onRemove && !(recurring && !removing) && <span className="h-8 w-8 shrink-0" aria-hidden="true" />}
      {onRemove && recurring && !removing && (
        <Tooltip title="Remove add-on" arrow>
          <button
            type="button"
            onClick={() => onRemove(addon)}
            aria-label={`Remove ${addon.name}`}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-[#b91c1c] transition-colors hover:bg-[#fdecea] focus:outline-none focus-visible:ring-2 focus-visible:ring-black"
          >
            <FiTrash2 className="h-4 w-4" aria-hidden="true" />
          </button>
        </Tooltip>
      )}
    </li>
  );
};

const AddonsSection = ({ data, plans, plansLoading = false, state, onNotice, onUpgrade }) => {
  const theme = useTheme();
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removeBusy, setRemoveBusy] = useState(false);
  const [removeError, setRemoveError] = useState("");
  const [pending, setPending] = useState(null);
  const [buyBusy, setBuyBusy] = useState(false);
  const [buyError, setBuyError] = useState("");
  // Back from Stripe Checkout: the button must not stay on "Working..."
  useResetOnReturn(() => setBuyBusy(false));
  // The prorated charge for a monthly add-on, asked for before anything is bought
  const [preview, setPreview] = useState({ status: "idle" });
  const [previewTick, setPreviewTick] = useState(0);

  const plan = data?.plan;
  const row = planOf(plan);
  const hasPlan = state !== PLAN_STATE.NONE && state !== PLAN_STATE.LEGACY;
  const paid = hasPlan && state !== PLAN_STATE.TRIAL;
  const offered = paid ? plans.find((item) => item.plan_id === row?.plan_id)?.addons || {} : {};
  const offeredTypes = Object.keys(offered);
  const mine = (data?.addons || []).filter((addon) => addon.status === "active" || addon.status === "canceling");
  const loading = !data || (paid && plansLoading);

  useEffect(() => {
    if (!pending || pending.addon.billing !== "recurring") {
      setPreview({ status: "idle" });
      return undefined;
    }
    let cancelled = false;
    setPreview({ status: "loading" });
    previewAddon(pending.type, pending.count)
      .then((body) => !cancelled && setPreview({ status: "ready", data: body }))
      .catch((err) => {
        if (cancelled) return;
        // No preview endpoint yet: say so and let the purchase go ahead, rather than blocking seats altogether
        const missing = [404, 405].includes(err?.response?.status);
        setPreview({ status: missing ? "unavailable" : "error", error: apiErrorMessage(err, "Could not work out the amount.") });
      });
    return () => {
      cancelled = true;
    };
  }, [pending, previewTick]);

  const confirmBuy = async () => {
    setBuyBusy(true);
    setBuyError("");
    try {
      const body = await buyAddon(pending.type, pending.count);
      if (body.checkout_url) {
        rememberCheckoutBaseline();
        window.location.assign(body.checkout_url);
        return;
      }
      onNotice(`Added ${pending.count} × ${pending.addon.name}.`);
      setPending(null);
    } catch (err) {
      setBuyError(BUY_ERRORS[apiErrorCode(err)] || apiErrorMessage(err, "Could not complete the purchase. Try again."));
    } finally {
      setBuyBusy(false);
    }
  };

  const closeBuy = () => {
    setPending(null);
    setBuyError("");
  };

  const confirmRemove = async () => {
    setRemoveBusy(true);
    setRemoveError("");
    try {
      const body = await removeAddon(removeTarget.id);
      setRemoveTarget(null);
      onNotice(`${removeTarget.name} removed. It stays available until ${formatDate(body.addon?.end_at)}.`);
    } catch (err) {
      setRemoveError(apiErrorMessage(err, "Could not remove the add-on. Try again."));
      // The list may be out of date (already removed elsewhere)
      if (["ADDON_NOT_FOUND", "INVALID_ADDON_STATE"].includes(apiErrorCode(err))) refreshPlan().catch(() => {});
    } finally {
      setRemoveBusy(false);
    }
  };

  const locked = (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-dashed bg-[#f8fafd] px-4 py-3" style={{ borderColor: theme.palette.secondary.v4 }}>
      <div className="flex items-center gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-accent-gray-light shadow-sm" aria-hidden="true">
          <FiLock className="h-4 w-4" />
        </span>
        <p className="text-sm">
          <span className="font-medium">{state === PLAN_STATE.TRIAL ? "Add-ons unlock on a paid plan." : "Choose a plan to see its add-ons."}</span>
          <span className="block text-xs text-accent-gray-light">Extra seats, AI credits and resume packs can be added on top of any paid plan.</span>
        </p>
      </div>
      {onUpgrade && <PrimaryButton onClick={onUpgrade}>{state === PLAN_STATE.TRIAL ? "Upgrade Plan" : "Buy Plan"}</PrimaryButton>}
    </div>
  );

  const pendingMetric = pending ? metricLabel(metricForAddon(pending.type)).toLowerCase() : "";
  const pendingRecurring = pending?.addon.billing === "recurring";
  const amountNow = preview.status === "ready" ? formatCents(preview.data.amount_due_cents, preview.data.currency) : "";
  const periodEnd = preview.data?.period_end || plan?.current_period_end;
  const monthly = pending ? pending.addon.amount_cents * pending.count : 0;

  return (
    <div id="addons" className="flex flex-col gap-3">
      <Section dense title="Add more to your plan" description="Extras on top of your plan's allowances.">
        {loading ? (
          <ul role="status" aria-label="Loading add-ons" className="space-y-2">
            {[0, 1, 2, 3].map((item) => (
              <li key={item}>
                <Skeleton className="h-10 w-full" />
              </li>
            ))}
          </ul>
        ) : !paid ? (
          locked
        ) : offeredTypes.length === 0 ? (
          <p className="text-sm text-accent-gray-light">No add-ons are offered for your plan.</p>
        ) : (
          <ul className="-my-2 divide-y">
            {offeredTypes.map((type) => (
              <AddonOffer key={type} type={type} addon={offered[type]} onBuy={setPending} />
            ))}
          </ul>
        )}
      </Section>

      {mine.length > 0 && (
        <Section dense title={`Your active add-ons (${mine.length})`}>
          <ul className="-my-2 divide-y">
            {mine.map((addon) => (
              <OwnedAddonRow key={addon.id} addon={addon} unitCents={offered[addon.addon_type]?.amount_cents} onRemove={setRemoveTarget} />
            ))}
          </ul>
        </Section>
      )}

      <OrgModal
        isOpen={Boolean(pending)}
        onClose={buyBusy ? () => {} : closeBuy}
        titleId="addon-buy-title"
        title="Confirm purchase"
        maxWidth="max-w-md"
        compact
        footer={
          <PrimaryButton onClick={confirmBuy} disabled={buyBusy || (pendingRecurring && ["loading", "error"].includes(preview.status))}>
            {buyBusy ? "Working..." : pendingRecurring ? (amountNow ? `Confirm and pay ${amountNow}` : "Confirm purchase") : "Continue to checkout"}
          </PrimaryButton>
        }
      >
        {pending && (
          <>
            <div className="flex items-center gap-3 rounded-xl bg-[#f3f6fb] p-3">
              <IconTile type={pending.type} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold">{pending.addon.name}</p>
                <p className="text-xs text-accent-gray-light">
                  {pending.count} × {formatCents(pending.addon.amount_cents)}
                  {pending.addon.units > 1 && ` · ${formatNumber(pending.addon.units * pending.count)} ${pendingMetric} in total`}
                </p>
              </div>
              <p className="shrink-0 text-right">
                <span className="text-xl font-bold">{formatCents(pending.addon.amount_cents * pending.count)}</span>
                <span className="block text-[11px] text-accent-gray-light">{pendingRecurring ? "per month" : "one time"}</span>
              </p>
            </div>
            {pendingRecurring ? (
              <>
                <div className="rounded-xl border p-3 text-sm" style={{ borderColor: theme.palette.secondary.v4 }}>
                  <div className="flex items-center justify-between gap-3">
                    <span>
                      Charged now
                      <span className="block text-xs text-accent-gray-light">Prorated{periodEnd ? ` until ${formatDate(periodEnd)}` : " for this period"}</span>
                    </span>
                    {preview.status === "ready" ? <span className="text-lg font-bold">{amountNow}</span> : preview.status === "loading" ? <Skeleton className="h-6 w-16" /> : <span className="text-accent-gray-light">—</span>}
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-3 border-t pt-2" style={{ borderColor: theme.palette.secondary.v4 }}>
                    <span>Then every month</span>
                    <span className="font-semibold">{formatCents(monthly)}</span>
                  </div>
                </div>
                {preview.status === "error" ? (
                  <ErrorBox>
                    {preview.error}{" "}
                    <button type="button" className="font-semibold underline" onClick={() => setPreviewTick((tick) => tick + 1)}>
                      Try again
                    </button>
                  </ErrorBox>
                ) : (
                  <p className="flex items-start gap-2 text-sm text-accent-gray-light" aria-live="polite">
                    <FiAlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-accent-blue" aria-hidden="true" />
                    {preview.status === "ready"
                      ? `You'll be charged ${amountNow} now to your card on file, then ${formatCents(monthly)}/month${pending.count > 1 ? ` (${formatCents(pending.addon.amount_cents)} each)` : ""}. Billed through Stripe.`
                      : preview.status === "unavailable"
                        ? `You'll be charged the prorated amount for this period now to your card on file, then ${formatCents(monthly)}/month. Billed through Stripe.`
                        : "Working out the prorated amount..."}
                  </p>
                )}
              </>
            ) : (
              <p className="flex items-start gap-2 text-sm text-accent-gray-light">
                <FiAlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-accent-blue" aria-hidden="true" />
                {`You'll pay on Stripe's secure checkout page. Anything unused by ${plan?.current_period_end ? formatDate(plan.current_period_end) : "the end of this period"} is lost.`}
              </p>
            )}
            <ErrorBox>{buyError}</ErrorBox>
          </>
        )}
      </OrgModal>

      <ConfirmDialog
        isOpen={Boolean(removeTarget)}
        onClose={() => setRemoveTarget(null)}
        onConfirm={confirmRemove}
        title="Remove add-on"
        message={
          <p>
            Stop billing for <span className="font-medium">{removeTarget?.name}</span> from the next period? It keeps counting until{" "}
            {formatDate(removeTarget?.end_at || plan?.current_period_end)}.
          </p>
        }
        confirmLabel="Remove add-on"
        busy={removeBusy}
        error={removeError}
      />
    </div>
  );
};

export default AddonsSection;
